import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
// Clsx
import clsx from 'clsx'
// Material UI
import Autocomplete from '@material-ui/lab/Autocomplete'
import TextField from '@material-ui/core/TextField'
import Input from '@material-ui/core/Input'
import InputBase from '@material-ui/core/InputBase'
import Box from '@material-ui/core/Box'
import Link from '@material-ui/core/Link'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import Tooltip from '@material-ui/core/Tooltip'
import Skeleton from '@material-ui/lab/Skeleton'
import Typography from '@material-ui/core/Typography'
import { makeStyles } from '@material-ui/core/styles'
// React Image
import ReactImage from 'react-image'
// Utils
import { formatNumber, formatNumberWithMultiplesOfFive, formatNumberWithMultiplesOfTwentyFive } from '../Utils/utils'
import { UserContext } from '../App/AppContext'

// Mutations
import {
  UPDATE_VIA_PORTS
} from '../Mutations/Mutations'

const useTooltipClasses = makeStyles(theme => ({
  text: {
    fontSize: theme.typography.generalFontSize - 2
  }
}))

const HtmlTooltip = props => {
  const { children, onHoverData = [] } = props
  const classes = useTooltipClasses()
  return (
    <Tooltip
      title={
        <div className={classes.tooltip}>
          {
            onHoverData.map(data => {
              return (
                <Typography key={data} className={classes.text} color='inherit'>{data}</Typography>
              )
            })
          }
        </div>
      }
    >
      {children}
    </Tooltip>
  )
}

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'nowrap',
    overflowX: 'scroll',
    overflowY: 'hidden',
    fontSize: '12px',
    paddingTop: '0px',
    paddingBottom: '1%',
    background: theme.palette.common.white,
    borderRadius: '10px'
  },
  table: {
    tableLayout: 'fixed'
  },
  routesColumn: {
    flex: '0 0 10%',
    maxWidth: '15%'
  },
  routesColumnTypography: {
    fontSize: theme.typography.generalFontSize
  },
  flag: {
    flex: '0 0 85%',
    marginLeft: '15%'
  },
  flagTypography: {
    fontSize: theme.typography.generalFontSize,
    fontWeight: 'bold'
  },
  smallCell: {
    fontSize: theme.typography.generalFontSize - 2,
    fontWeight: 'light'
  },
  boldCell: {
    fontWeight: 'bold'
  },
  cellName: {
    minWidth: 100,
    maxWidth: 100,
    // fontWeight: 'bold'
    fontWeight: 400
  },
  quartersCell: {
    // borderRight: '1px solid #000',
    border: 'none',
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.1)'
    }
  },
  baseRoute: {
    backgroundColor: '#70FFC1'
  },
  divider: {
    display: 'block',
    height: 20
  },
  link: {
    color: theme.palette.text.secondary,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline'
    }
  },
  quarter: {
    borderRight: '1px solid rgba(123, 129, 130, 1)'
  },
  errorClass: {
    color: 'red'
  },
  countryStyle: {
    display: 'flex',
    alignItems: 'center'
  },
  countryStyleTypography: {
    marginLeft: 8,
    fontSize: theme.typography.generalFontSize + 1,
    fontWeight: 'bold'
  },
  routeResultsCell: {
    cursor: 'pointer',
    '&:hover': {
      color: '#db5461'
    }
  },
  heatMapCell: {
    cursor: 'pointer',
    '&:hover': {
      // color: '#f4f2f4'
      color: 'white'
    }
  },
  flag: {
    width: 32
  },
  heatMapLowGreen: {
    backgroundColor: '#97e7a4',
    color: '#37392E'
  },
  heatMapMediumGreen: {
    backgroundColor: '#6bd47c',
    color: '#37392E'
  },
  heatMapHighGreen: {
    backgroundColor: '#3aac4d',
    color: '#37392E'
  },
  heatMapOutGreen: {
    backgroundColor: '#2c943d',
    color: '#37392E'
  },
  heatMapLowRed: {
    backgroundColor: '#ffc8ba',
    color: '#37392E'
  },
  heatMapMediumRed: {
    backgroundColor: '#f2856b',
    color: '#37392E'
  },
  heatMapHighRed: {
    backgroundColor: '#f36746',
    color: '#37392E'
  },
  heatMapOutRed: {
    backgroundColor: '#f7532d',
    color: '#37392E'
  },
  heatMapNoChange: {
    backgroundColor: 'white',
    color: '#37392E',
    cursor: 'pointer',
  },
  noResult: {
    backgroundColor: '#90CAF9',
    color: '#37392E'
  }
}))

const ClientResultHorizontal = props => {
  const { adcom, client, clientName, clientRoutes, hasNextPage, report, setContextState, quarterArray, heatMap, resultsToCompare, heatMapHigh, heatMapMedium, heatMapLow } = props
  const classes = useStyles()
  // const countries = clientRoutes.clientRoutes
  const headers = clientRoutes.headers
  const { setUser } = React.useContext(UserContext)

  const [countries, setCountries] = React.useState(clientRoutes.clientRoutes)

  const recalculateCountries = props => {
    const a = new Date(Date.now())
    const { adcom, countries } = props

    const newCountries = countries.map((country, i) => {
      const newRoutes = country.routes.map((route, i) => {
        let first = formatNumber({ number: parseFloat(country.routes[0].routeResults[0].rate) * (1 + adcom / 100) });
        ({ formattedRate: first } = formatNumberWithMultiplesOfTwentyFive({ formattedRate: first }))
        first = formatNumber({ number: first })
        let spread = route.routeResults[0].rate
        try {
          let current = parseFloat(spread)
          current = formatNumber({ number: current * (1 + adcom / 100) })
          const { formattedRate } = formatNumberWithMultiplesOfTwentyFive({ formattedRate: current })
          current = formatNumber({ number: formattedRate })
          // Calculate rate with adcom
          spread = formatNumber({
            number: parseFloat(current) - parseFloat(first)
          })
        } catch (e) {
          spread = route.spread
        }
        const newRouteResults = route.routeResults.map((result, j) => {
          let text = result.rate
          try {
            // Calculate rate with adcom
            let formattedRate = formatNumber({ number: parseFloat(text) * (1 + adcom / 100) });
            // Format the rate with decimals with multiple of five
            ({ formattedRate } = formatNumberWithMultiplesOfTwentyFive({ formattedRate }))
            text = formatNumber({ number: formattedRate })
          } catch (e) {
            text = result.text
          }
          return {
            ...result,
            text
          }
        })

        return {
          ...route,
          routeResults: newRouteResults,
          spread
        }
      })
      return {
        ...country,
        routes: newRoutes
      }
    })
    const b = new Date(Date.now())
    return newCountries
  }

  React.useEffect(() => {
    const newCountries = recalculateCountries({ adcom, countries })
    setCountries(newCountries)
  }, [adcom, setCountries])

  React.useEffect(() => {
    if (countries.length !== clientRoutes.clientRoutes) {
      const newCountries = recalculateCountries({ adcom, countries: clientRoutes.clientRoutes })
      setCountries(newCountries)
    }
  }, [clientRoutes])

  return (
    <Box className={classes.root}>
      <Table size='small'>
        {
          countries.map((country, i) => {
            const flag = country.flag
            const name = country.name
            const routes = country.routes
            return (
              <Fragment key={i}>
                <TableHead>
                  <TableRow>
                    <TableCell colSpan={8} style={{ paddingTop: '15px'}}>
                      <div className={classes.countryStyle}>
                        <ReactImage className={classes.flag} src={`/flags/${flag}.png`} alt={flag} />
                        <Typography className={classes.countryStyleTypography} display='inline'>
                          {country.name}
                        </Typography>
                      </div>
                      {/* <ReactImage src={country.flag} alt={country.name} /> */}
                    </TableCell>
                    {/* <TableCell>{country.name} </TableCell> */}
                  </TableRow>
                  <TableRow>
                    {
                      headers.headersData.map((header, i) => {
                        return (
                          <TableCell key={`${header.text}${i}`} align={header.align} className={classes[header.className]}>{header.text}</TableCell>
                        )
                      })
                    }
                    {
                      headers.headersPositionDates.map((header, i) => {
                        return (
                          <TableCell key={`${header.text}${i}`} align={header.align} className={classes[header.className]}>{header.text}</TableCell>
                        )
                      })
                    }
                  </TableRow>
                </TableHead>
                <TableBody>
                  {
                    routes.map((route, i) => {
                      const onContextMenu = (event) => setContextState({
                        event,
                        selectedRow: route,
                        type: 'open'
                      })
                      return (
                        <TableRow index={i} key={i} className={[classes.quartersCell, classes[route.baseRouteStyles.className]]} onContextMenu={onContextMenu}>
                          {
                            headers.headersData.map((header, j) => {
                              const data = route.routeData[header.key]
                              if (j === 0) {
                                return (
                                  <TableCell key={`${data.text}${j}`} >
                                    <Link
                                      target='_blank'
                                      className={classes[data.className]}
                                      underline='none'
                                      color='inherit'
                                      href={`/calculation-page/${route.id ? route.id : '#'}`}
                                    // onClick={(e) => { e.preventDefault() }}
                                    >
                                      {data.text}
                                    </Link>
                                  </TableCell>
                                )
                              }
                              if (header.key === 'loadDischTerm') {
                                return (
                                  <HtmlTooltip key={`${data.text}${j}`} onHoverData={route.onHoverData}>
                                    <TableCell align={data.align} className={classes[data.className]}>{data.text}</TableCell>
                                  </HtmlTooltip>
                                )
                              }
                              if (header.key === 'viaLadenPort') {
                                return (
                                  <TableCell key={`${data.text}${j}`} align={data.align} className={classes[data.className]}>{data.text}</TableCell>
                                )
                              }
                              if (header.key === 'spread') {
                                return (
                                  <TableCell key={`${data.text}${j}`} align={data.align} className={clsx(classes[data.className], classes.quarter)}>{data.text}</TableCell>
                                )
                              }
                              return (
                                <TableCell key={`${data.text}${j}`} align={data.align} className={classes[data.className]}>{data.text}</TableCell>
                              )
                            })
                          }
                          {
                            route.routeResults.map((result, j) => {
                              // console.log({ result })
                              const needsBorderQuarter = [10, 7, 4, 0]
                              const quarter = quarterArray.includes(j)
                              const text = result.rate

                              let previousRate
                              if (heatMap && resultsToCompare[route.id]) {
                                previousRate = resultsToCompare[route.id][j]
                              }

                              const noPreviousRate = !previousRate && heatMap

                              const difference = parseFloat(result.rate) - previousRate

                              const diffIsNumber = !isNaN(difference)
                              const heatMapLowGreen = diffIsNumber && difference > 0 && difference <= heatMapLow.current
                              const heatMapMediumGreen = diffIsNumber && difference > heatMapLow.current && difference <= heatMapMedium.current
                              const heatMapHighGreen = diffIsNumber && difference > heatMapMedium.current && difference <= heatMapHigh.current
                              const heatMapOutGreen = diffIsNumber && difference > heatMapHigh.current

                              const heatMapNoChange = diffIsNumber && difference === 0

                              const heatMapLowRed = diffIsNumber && difference < 0 && difference >= -heatMapLow.current
                              const heatMapMediumRed = diffIsNumber && difference < -heatMapLow.current && difference >= -heatMapMedium.current
                              const heatMapHighRed = diffIsNumber && difference < -heatMapMedium.current && difference >= -heatMapHigh.current
                              const heatMapOutRed = diffIsNumber && difference < -heatMapHigh.current

                              const onClick = () => {
                                setUser({ type: 'voyage-offer', value: true, voyageRoute: result.route, voyageRouteName: result.routeName, voyageClient: { id: client, name: clientName }, voyageDate: result.positionDate, adcomFromClientResult: adcom, voyageFromClientResults: true })
                              }
                              return (
                                <TableCell
                                  key={`${text}${j}`}
                                  align={result.align}
                                  className={classes[result.className]}
                                  className={clsx({
                                    // To-do: set the colors
                                    [classes.routeResultsCell]: !heatMap || heatMapNoChange,
                                    [classes.heatMapCell]: heatMap && !heatMapNoChange,
                                    [classes.quarter]: quarter,
                                    [classes.heatMapLowGreen]: heatMapLowGreen,
                                    [classes.heatMapMediumGreen]: heatMapMediumGreen,
                                    [classes.heatMapHighGreen]: heatMapHighGreen,
                                    [classes.heatMapOutGreen]: heatMapOutGreen,
                                    [classes.heatMapLowRed]: heatMapLowRed,
                                    [classes.heatMapMediumRed]: heatMapMediumRed,
                                    [classes.heatMapHighRed]: heatMapHighRed,
                                    [classes.heatMapOutRed]: heatMapOutRed,
                                    [classes.noResult]: noPreviousRate,
                                    [classes.heatMapNoChange]: heatMapNoChange
                                    
                                  })}
                                  onClick={onClick}
                                >
                                  {result.text}
                                </TableCell>
                              )
                            })
                          }
                        </TableRow>
                      )
                    })
                  }
                </TableBody>
              </Fragment>
            )
          })
        }

      </Table>
      {
        hasNextPage && (
          <Table style={{ marginTop: 8 }}>
            <TableHead>
              <TableRow>
                <TableCell colSpan={8}>
                  <div className={classes.countryStyle}>
                    <Skeleton animation='wave' variant='rect' width={32} height={32} />
                    <Typography className={classes.countryStyleTypography} display='inline'>
                      <Skeleton animation='wave' width={200} />
                    </Typography>
                  </div>
                  {/* <ReactImage src={country.flag} alt={country.name} /> */}
                </TableCell>
                {/* <TableCell>{country.name} </TableCell> */}
              </TableRow>
              <TableRow>
                <TableCell>
                  <Skeleton animation='wave' width='100%' />
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>
                  <Skeleton animation='wave' width='100%' />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <Skeleton animation='wave' width='100%' />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <Skeleton animation='wave' width='100%' />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        )
      }
    </Box>
  )
}

ClientResultHorizontal.propTypes = {

}

export default ClientResultHorizontal
